import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { history } from "../../helpers/history";
import { Box, Grid } from '@mui/material';
import styled from "styled-components";
import CronogramaEtapas from "./Components/CronogramaEtapas";
import ProgramaResumo from "./Components/ProgramaResumo";
import { useContatosActions } from "../../states/contatos/contatos.actions";
import { calcularTempoProjeto } from "../../commons/utils/tempoProjeto";
import { useAcoesActions } from "../../states/historicoAcoes/acoes/acoes.actions";
import HistoricoAcoes from "./Components/HistoricoReunioes";
import { ButtonStato } from "../../commons/styleds/buttons";
import generatePDF, { Margin, Resolution } from "react-to-pdf";
import DetalhesPDF from './Components/DetalhesPDF';
import { esES, ptBR } from "@mui/material/locale";
import { useTranslation } from "../../commons/useTranslasion";
import { useDateFormatters } from "../../utils/useDateFormatters";

export interface IParams {
  id: string;
}

interface Etapa {
  nome: string;
  data: string | null;
}

interface Cronograma {
  Think: Etapa[];
  Plan: Etapa[];
  GoToMarket: Etapa[];
}

const Detalhes: React.FC = () => {
      const { translate } = useTranslation();
      const { formatarData } = useDateFormatters();
      const lang = process.env["REACT_APP_LANG"] ?? "pt-br";
      let locale = ptBR;
      switch (lang.slice(0, 2)) {
        case "es":
          locale = esES
          break;
      }
  const { id } = useParams<IParams>();
  const [comanda, setComanda] = useState<any>();
  const targetRef = useRef<HTMLDivElement>(null);

  const actionsContatos = useContatosActions();
  const actionsHistoricoAcoes = useAcoesActions();

  useEffect(() => {
    const fetchData = async () => {
      if (!id) history.goBack();

      try {
        const value = await actionsContatos.getContatoDetalhes(id);

        const findDataPrevista = (etapaId: number) => {
          const reuniao: any = value?.comanda.comandaEtapaReunioes?.find(
            (reuniao: any) => reuniao.tbEtapaId === etapaId
          );
          return reuniao ? formatarData(reuniao.dtPrevista) : (translate("semData"));
        };
        
        const mapHistoricoAcoes = async () => {
          const historicoAcoes: any[] = [];
          const result: any = await actionsHistoricoAcoes.getHistoricoAcoes(value?.comanda?.id);

          result.resultado.map((item: any) => {
            historicoAcoes.push({
              data: formatarData(item.dtCadastro),
              dataSorting: new Date(item.dtCadastro),
              nome: item.idTipoNavigation.nome,
              descricao: item.observacoes
            });
          });

          return historicoAcoes.sort((a, b) => b.dataSorting.getTime() - a.dataSorting.getTime());
        };

        const mapEtapasToCronograma = (etapas: any[]): Cronograma => {
          const cronograma: Cronograma = {
            Think: [],
            Plan: [],
            GoToMarket: [],
          };

          etapas.forEach((etapa) => {
            const nomeEtapa = etapa.nome;
            const dataEtapa = findDataPrevista(etapa.id);

            if (etapa.idMetodologia === 1) {
              cronograma.Think.push({ nome: nomeEtapa, data: dataEtapa });
            } else if (etapa.idMetodologia === 2) {
              cronograma.Plan.push({ nome: nomeEtapa, data: dataEtapa });
            } else if (etapa.idMetodologia === 3) {
              cronograma.GoToMarket.push({ nome: nomeEtapa, data: dataEtapa });
            }
          });

          return cronograma;
        };

        const historico = await mapHistoricoAcoes();

        const resource = {
          programa: {
            nome: value?.contato?.nome,
            programa: value?.comanda?.plano,
            prazo: calcularTempoProjeto(
              new Date(value?.comanda?.dataInicio!),
              new Date(value?.comanda?.dataFim!)
            ),
            inicio: formatarData(value?.comanda?.dataInicio),
            termino: formatarData(value?.comanda?.dataFim),
            status: value?.comanda?.status?.nome,
            cargo: value?.contato?.ultimoCargo,
            responsavel: value?.comanda?.consultorMaster?.nome,
          },
          cronograma: mapEtapasToCronograma(value?.comanda?.etapas || []),
          historico: historico,
        };

        setComanda(resource);
      } catch (error) {
        console.error(translate("erroAoBuscarDetalhesDaComanda"), error);
      }
    };

    fetchData();
  }, [id]);

  const handleDownloadPDF = async () => {
    if (!targetRef.current) return;
  
    await generatePDF(() => targetRef.current!, {
      filename: `STATO_Relatorio-Individual-${comanda?.programa?.nome}.pdf`,
      method: 'save',
      resolution: Resolution.NORMAL,
      page: {
        margin: Margin.SMALL,
        format: 'a4',
        orientation: 'landscape'
      },
      canvas: {
        mimeType: 'image/jpeg',
        qualityRatio: 0.98
      },
      overrides: {
        pdf: {
          compress: true,
          unit: 'mm',
          format: 'a4'
        },
        canvas: {
          scale: 2.5,
          useCORS: true,
          logging: false
        }
      }
    });
  };

  return (
    <>
      <BoxContato sx={{ width: '100%' }}>
        {comanda && (
          <>
            <ButtonStato
              variant="contained"
              onClick={handleDownloadPDF}
              sx={{ margin: '20px' }}
            >
              {translate("exportarPDF")}
            </ButtonStato>

            <Box>
              <ProgramaResumo programa={comanda.programa} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <CronogramaEtapas cronograma={comanda.cronograma} />
                </Grid>
                <Grid item xs={8}>
                  <HistoricoAcoes historico={comanda.historico} />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </BoxContato>

      <div 
        style={{ 
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '308mm',
          height: 'auto',
          backgroundColor: 'white',
          margin: 0,
          padding: 0,
          overflow: 'hidden'
        }}
      >
        <div ref={targetRef} style={{ width: '100%', height: '100%' }}>
          {comanda && <DetalhesPDF comanda={comanda} />}
        </div>
      </div>
    </>
  );
}

const BoxContato = styled(Box)`
  width: 100%;
  margin: 20px;
  border-radius: 16px !important;
  background: #f5f5f5;
  border: 1px solid #aaa;
`;

export default Detalhes;
